import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, ShieldAlert, ArrowRight, BookOpen } from "lucide-react";
import { PageHero } from "@/components/site/PageBits";
import { BookCallButton } from "@/components/site/BookCall";
import { Spinner } from "@/components/site/ProtectedRoute";
import { api, formatApiError } from "@/lib/api";
import { useSeo, pageSeo } from "@/lib/seo";

export default function AvenueCoursePage() {
  const { slug } = useParams();
  const [a, setA] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { api.get(`/online-courses/${slug}`).then((r) => setA(r.data)).catch((e) => setErr(formatApiError(e))); }, [slug]);
  useSeo(pageSeo(`/online-courses/${slug}`));
  if (err) return <p className="text-center py-24 text-rose-600" data-testid="avenue-error">{err}</p>;
  if (!a) return <Spinner />;
  const acc = a.access;
  return (
    <article data-testid="avenue-course-page">
      <PageHero eyebrow={`AVENUE ${String(a.number).padStart(2, "0")} · COURSE BOOKLET · Esc DIGITAL NOMAD VILLAGE`} title={a.title} subtitle={a.tagline}>
        {acc?.has ? <Link to={`/learn/${a.slug}/1`} className="inline-flex items-center gap-2 rounded-xl bg-[#F08A3C] text-white px-6 py-3 font-semibold" data-testid="avenue-open-btn"><BookOpen className="h-4 w-4" /> Open the course</Link>
          : <BookCallButton source={`avenue-${a.slug}`} light testId="avenue-call-btn" label="Book a free call to join" />}
        <span className="text-sm text-[#F5ECD7]/80" data-testid="avenue-price">${a.price} · or free with a 7-day or 30-day retreat at Esc</span>
      </PageHero>
      <section className="max-w-6xl mx-auto px-4 sm:px-8 py-14 grid lg:grid-cols-[1.4fr_1fr] gap-10">
        {a.weeks ? (
        <div>
          <h2 className="text-2xl font-bold text-[#0B2A4A]">Your course parts</h2>
          <ol className="mt-6 grid sm:grid-cols-2 gap-4">
            {a.weeks.map((w) => (
              <li key={w.n} className="bg-white rounded-2xl border border-[#0B2A4A]/10 p-5" data-testid={`avenue-week-${w.n}`}>
                <p className="font-mono-ui text-[11px] tracking-[0.25em] text-[#F08A3C]">PART {w.n} · {w.week.toUpperCase()}</p>
                <h3 className="mt-1 font-bold text-[#0B2A4A]">{w.name}: {w.title}</h3>
                <p className="mt-1 text-sm text-slate-600">Target: {w.goal}</p>
              </li>
            ))}
          </ol>
          <div className="mt-8 flex gap-3 rounded-2xl bg-[#F5ECD7] p-5" data-testid="avenue-golden-rule"><ShieldAlert className="h-5 w-5 text-[#F08A3C] shrink-0" /><p className="text-sm text-slate-700"><strong>The golden rule.</strong> {a.golden_rule}</p></div>
        </div>
        ) : (
        <div className="rounded-2xl bg-white border border-[#0B2A4A]/10 p-6 sm:p-8 self-start" data-testid="avenue-call-for-info">
          <h2 className="text-2xl font-bold text-[#0B2A4A]">Want the full course outline?</h2>
          <p className="mt-3 text-slate-700 leading-relaxed">The lessons, worksheets and resources open on your account once your place is confirmed. Book a free 20-minute call with Greg to hear exactly what's inside and find the right package for you, in person at Esc or online.</p>
          <div className="mt-5"><BookCallButton source={`avenue-info-${a.slug}`} testId="avenue-info-call-btn" label="Book a free call for more info" /></div>
        </div>
        )}
        <aside className="flex flex-col gap-5">
          <div className="rounded-2xl bg-[#0B2A4A] text-[#F5ECD7] p-6">
            <h2 className="font-semibold text-white">{a.weeks ? "By Day 30 you will have" : "A few things you'll leave with"}</h2>
            <ul className="mt-3 flex flex-col gap-2 text-sm">{a.outcomes.map((o) => <li key={o} className="flex gap-2"><CheckCircle2 className="h-4 w-4 mt-0.5 text-[#F08A3C] shrink-0" />{o}</li>)}</ul>
          </div>
          {a.folder_files && <div className="rounded-2xl bg-white border border-[#0B2A4A]/10 p-6">
            <h2 className="font-semibold text-[#0B2A4A]">In your Esc resource folder</h2>
            <p className="mt-2 text-sm text-slate-600">{a.folder_files.join(" · ")}</p>
          </div>}
          <Link to="/online-courses" className="inline-flex items-center gap-1 text-sm font-semibold text-[#147C88]" data-testid="avenue-all-link">All online courses <ArrowRight className="h-4 w-4" /></Link>
        </aside>
      </section>
    </article>
  );
}
